import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cqSpeedQuery, runSpeedTest, type CqSpeedGrade, type CqSpeedPage, type CqSpeedTest } from '../api/connectionQuality'
import { Spinner } from '../ui/Spinner'
import { CQ_COLORS, CqChart, type CqChartSeries } from './CqChart'
import { hourLabel } from './CqSpeedDaily'
import './CqPath.css'
import './CqSpeed.css'

/**
 * Sectiunea "Speed" a ecranului Connection Quality (faza 4): ultimul test de
 * viteza - download, upload, latenta in repaus fata de cea sub sarcina, RPM -
 * nota de bufferbloat A-F, istoricul pe 30 de zile si butonul "Run speed test
 * now" (comanda CQS).
 *
 * Nota se pune pe server (Speed.php, pragurile acolo); ecranul doar o arata.
 * Un test cazut isi arata eroarea; cifrele de sus vin din ultimul test reusit.
 */

const GRADE_TONE: Record<CqSpeedGrade, string> = { A: 'is-a', B: 'is-b', C: 'is-c', D: 'is-d', F: 'is-f' }

function fmt(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined) return '-'
  const f = Math.pow(10, digits)
  return String(Math.round(n * f) / f)
}

function mbps(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-'
  return n >= 100 ? fmt(n) : fmt(n, 1)
}

/** Ce inseamna fiecare nota, din pragurile serverului: "A: under +5 ms". */
export function gradeScale(grades: CqSpeedPage['grades']): string {
  const order = ['A', 'B', 'C', 'D'] as const
  return [...order.map((g) => `${g}: under +${grades[g]} ms`), `F: +${grades.D} ms or more`].join(' · ')
}

/**
 * `tiles` = false in vederea Grid: acolo cifrele ultimului test stau deja in
 * cardurile categoriei Speed (CqGrid.tsx), deci sectiunea nu le mai repeta -
 * ramane istoricul, butonul, avertismentul de banda si explicatia bufferbloat.
 */
export function CqSpeed({ id, tiles = true }: { id: number; tiles?: boolean }) {
  const [runError, setRunError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { data, error } = useQuery(cqSpeedQuery(id))

  const run = useMutation({
    mutationFn: () => runSpeedTest(id),
    onSuccess: (page) => {
      setRunError(null)
      queryClient.setQueryData(cqSpeedQuery(id).queryKey, page)
    },
    onError: (e: Error) => {
      setRunError(e.message)
      void queryClient.invalidateQueries({ queryKey: cqSpeedQuery(id).queryKey })
    },
  })

  if (error) {
    return (
      <div className="cq-speed">
        <h5>Speed</h5>
        <div className="cq-note">The speed tests could not be loaded: {error.message}</div>
      </div>
    )
  }
  if (!data) return <Spinner />

  const busy = data.run.state !== 'idle' || run.isPending
  const buttonText =
    data.run.state === 'pending' ? 'Waiting for the agent…' : data.run.state === 'running' ? 'Testing…' : 'Run speed test now'
  const good = data.lastGood
  const failed = data.last && data.last.error ? data.last : null

  const speed: CqChartSeries[] = [
    { name: 'Download', color: CQ_COLORS[0], points: data.history.map((p) => ({ t: p.at, v: p.down_mbps })) },
    { name: 'Upload', color: CQ_COLORS[1], points: data.history.map((p) => ({ t: p.at, v: p.up_mbps })) },
  ]

  return (
    <section className="cq-speed" data-section="speed">
      <div className="cq-path-head">
        <h5>
          <i className="fas fa-tachometer-alt" aria-hidden="true"></i> Speed
        </h5>
        <div className="cq-path-tools">
          <span className="cq-muted cq-speed-daily-state">
            {data.auto ? `Daily speed test at ${hourLabel(data.hour)} device time` : 'Daily speed test is off'}
          </span>
          {data.canRun ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={busy || !data.enabled}
              title={!data.enabled ? 'Connection Quality is turned off for this device' : undefined}
              onClick={() => run.mutate()}
            >
              {busy ? <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> : <i className="fas fa-play" aria-hidden="true"></i>}{' '}
              {buttonText}
            </button>
          ) : null}
        </div>
      </div>
      {data.canRun ? (
        <p className="cq-speed-warn">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> A speed test uses the customer&apos;s bandwidth: up to about
          25 MB in each direction, for about 20 seconds, and it may slow down whatever else is running on that connection.
        </p>
      ) : null}
      {runError ? <div className="alert alert-warning cq-path-alert">{runError}</div> : null}
      {data.run.state !== 'idle' && data.run.since ? (
        <div className="cq-note">Speed test requested at {data.run.since}. The result appears here when the agent sends it.</div>
      ) : null}
      {failed ? (
        <div className="alert alert-warning cq-path-alert" data-speed-error={failed.id}>
          The last speed test ({failed.at}, {failed.reason}) failed: {failed.error}
        </div>
      ) : null}

      {good ? (
        <SpeedResult test={good} scale={gradeScale(data.grades)} explain={data.explain} tiles={tiles} />
      ) : (
        <div className="cq-muted cq-path-empty">
          No speed test in the last 30 days.
          {data.canRun ? ' You can run one now, or turn on the daily speed test in Agent Settings.' : ''}
        </div>
      )}

      {data.history.length > 0 ? (
        <CqChart title="Speed history" unit="Mbps" series={speed} height={200} from={data.from} to={data.to} bucketS={data.bucket_s} />
      ) : null}

      {data.recent.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-condensed cq-speed-tests">
            <thead>
              <tr>
                <th>When</th>
                <th>Why</th>
                <th className="num">Download</th>
                <th className="num">Upload</th>
                <th className="num">Idle</th>
                <th className="num">Under load</th>
                <th className="num">Bufferbloat</th>
                <th>Server</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((t) => (
                <tr key={t.id} data-test={t.id} className={t.error ? 'is-error' : undefined}>
                  <td>{t.at}</td>
                  <td>{t.reason}</td>
                  {t.error ? (
                    <td colSpan={5} className="cq-speed-error">
                      {t.error}
                    </td>
                  ) : (
                    <>
                      <td className="num">{mbps(t.down_mbps)} Mbps</td>
                      <td className="num">{mbps(t.up_mbps)} Mbps</td>
                      <td className="num">{fmt(t.idle_ms)} ms</td>
                      <td className="num">{fmt(worse(t))} ms</td>
                      <td className="num">{t.grade ? <Grade grade={t.grade} small /> : '-'}</td>
                    </>
                  )}
                  <td>{t.server ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}

function worse(t: CqSpeedTest): number | null {
  const loaded = [t.loaded_down_ms, t.loaded_up_ms].filter((v): v is number => v !== null)
  return loaded.length ? Math.max(...loaded) : null
}

function Grade({ grade, small }: { grade: CqSpeedGrade; small?: boolean }) {
  return (
    <span className={`cq-speed-grade ${GRADE_TONE[grade]}${small ? ' is-small' : ''}`} data-grade={grade}>
      {grade}
    </span>
  )
}

/** Cifrele unui test reusit: aici si pe tichet. */
export function SpeedResult({
  test,
  scale,
  explain,
  tiles = true,
}: {
  test: CqSpeedTest
  scale?: string
  explain?: string
  tiles?: boolean
}) {
  return (
    <div className="cq-speed-result" data-speed={test.id}>
      {tiles ? (
        <div className="cq-speed-tiles">
          <div className="cq-speed-tile">
            <span className="cq-speed-k">Download</span>
            <span className="cq-speed-v">
              <b>{mbps(test.down_mbps)}</b> Mbps
            </span>
          </div>
          <div className="cq-speed-tile">
            <span className="cq-speed-k">Upload</span>
            <span className="cq-speed-v">
              <b>{mbps(test.up_mbps)}</b> Mbps
            </span>
          </div>
          <div className="cq-speed-tile">
            <span className="cq-speed-k">Latency idle / under load</span>
            <span className="cq-speed-v">
              <b>{fmt(test.idle_ms)}</b> / <b>{fmt(test.loaded_down_ms)}</b> down, <b>{fmt(test.loaded_up_ms)}</b> up ms
            </span>
          </div>
          <div className="cq-speed-tile">
            <span className="cq-speed-k">Responsiveness</span>
            <span className="cq-speed-v">
              <b>{fmt(test.rpm)}</b> RPM
            </span>
          </div>
          <div className="cq-speed-tile">
            <span className="cq-speed-k">Bufferbloat</span>
            <span className="cq-speed-v">
              {test.grade ? <Grade grade={test.grade} /> : '-'}
              {test.bloat_ms !== null ? <span className="cq-muted"> +{fmt(test.bloat_ms)} ms</span> : null}
            </span>
          </div>
        </div>
      ) : null}
      {explain ? (
        <p className="cq-speed-explain">
          <i className="fas fa-info-circle" aria-hidden="true"></i> Bufferbloat: {explain.charAt(0).toLowerCase() + explain.slice(1)}
          {scale ? <span className="cq-muted"> ({scale})</span> : null}
        </p>
      ) : null}
      <div className="cq-muted cq-speed-meta">
        {test.at} · {test.reason}
        {test.server ? ` · ${test.server}` : ''}
        {test.streams ? ` · ${test.streams} streams` : ''}
        {test.duration_s ? ` · ${fmt(test.duration_s)} s` : ''}
      </div>
    </div>
  )
}

/** Ultimul test de viteza dinaintea tichetului, pe un rand (TicketConnectionQuality). */
export function TicketSpeedLine({ test }: { test: CqSpeedTest }) {
  return (
    <div className="cq-note cq-speed-ticket" data-section="speed">
      <i className="fas fa-tachometer-alt" aria-hidden="true"></i> Speed test {test.at} ({test.reason}): {mbps(test.down_mbps)} down /{' '}
      {mbps(test.up_mbps)} up Mbps · latency {fmt(test.idle_ms)} ms idle, {fmt(worse(test))} ms under load
      {test.grade ? (
        <>
          {' '}
          · bufferbloat <Grade grade={test.grade} small />
        </>
      ) : null}
    </div>
  )
}
